import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Select,
  Textarea,
  Box,
  Text,
  useColorModeValue,
  FormErrorMessage,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createInvoice, createAdminInvoice, createDraftInvoice, createAdminDraftInvoice } from "features/invoices/actions";
import { useToastNotification } from "utils/toast-utils";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const invoiceSchema = (isAdmin, isDraft = false) =>
  Yup.object().shape({
    ...(isAdmin && {
      clientId: isDraft ? Yup.string() : Yup.string().required("Client is required"),
    }),
    referenceNumber: isDraft ? Yup.string() : Yup.string().required("Reference number is required"),
    date: isDraft ? Yup.string() : Yup.string().required("Date is required"),
    amount: isDraft
      ? Yup.number().min(0, "Amount must be greater than 0")
      : Yup.number()
          .required("Amount is required")
          .min(0, "Amount must be greater than 0"),
    debtorName: isDraft
      ? Yup.string()
      : Yup.string()
          .required("Debtor name is required")
          .typeError("Debtor name must be a string"),
    notes: isDraft ? Yup.string() : Yup.string().required("Notes are required"),
    expectedDueDate: isDraft
      ? Yup.string()
      : Yup.string()
          .required("Expected due date is required")
          .test(
            "is-after-date",
            "Due date must be after creation date",
            function (value) {
              return !this.parent.date || !value || value >= this.parent.date;
            }
          ),
    files: isDraft
      ? Yup.array()
      : Yup.array()
          .min(1, "At least one file is required")
          .required("Files are required"),
  });

function toDateInputValue(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateForDisplay(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const CreateInvoiceModal = ({
  isOpen,
  onClose,
  isAdmin = false,
  clients = [],
  isLoading = false,
  initialValues = null,
  isEditingDraft = false,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const [isDragging, setIsDragging] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleClose = () => {
    if (formik.dirty) {
      setShowExitDialog(true);
    } else {
      formik.resetForm();
      onClose();
    }
  };

  const formik = useFormik({
    initialValues: initialValues
      ? {
          ...initialValues,
          ...(isAdmin && { clientId: initialValues.userId }),
          date: toDateInputValue(initialValues.date),
          expectedDueDate: toDateInputValue(initialValues.expectedDueDate),
          files: [],
        }
      : {
          ...(isAdmin && { clientId: "" }),
          referenceNumber: "",
          date: "",
          amount: "",
          debtorName: "",
          notes: "",
          expectedDueDate: "",
          files: [],
        },
    validationSchema: invoiceSchema(isAdmin),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const action = isAdmin ? createAdminInvoice : createInvoice;
        const payload = {
          ...values,
          ...(isAdmin && { userId: values.clientId }),
          draftId: isEditingDraft ? initialValues._id : "",
        };
        
        await dispatch(action(payload)).unwrap();
        showToast("Success", "Invoice created successfully", "success");
        formik.resetForm();
        onClose();
      } catch (error) {
        showToast(
          "Error",
          error.message || "Failed to create invoice",
          "error"
        );
      }
    },
  });

  const handleSaveAsDraft = async () => {
    setIsSavingDraft(true);
    try {
      const values = formik.values;
      const action = isAdmin ? createAdminDraftInvoice : createDraftInvoice;
      const payload = {
        ...values,
        ...(isAdmin && { userId: values.clientId }),
        draftId: isEditingDraft ? initialValues._id : null,
      };
      
      await dispatch(action(payload)).unwrap();
      showToast("Success", "Invoice saved as draft", "success");
      formik.resetForm();
      setShowExitDialog(false);
      onClose();
    } catch (error) {
      showToast(
        "Error",
        error.message || error.response?.data?.message || "Failed to save draft",
        "error"
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDiscardChanges = () => {
    formik.resetForm();
    setShowExitDialog(false);
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type === 'application/pdf' || 
      file.type === 'image/png' || 
      file.type === 'image/jpeg'
    );
    
    if (files.length > 0) {
      const totalFiles = formik.values.files.length + files.length;
      if (totalFiles > 5) {
        showToast("Error", "Maximum 5 files allowed", "error");
        return;
      }
      formik.setFieldValue("files", [...formik.values.files, ...files]);
    }
  };

  const handleDateChange = (date, fieldName) => {
    if (!date) {
      formik.setFieldValue(fieldName, '');
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    formik.setFieldValue(fieldName, `${year}-${month}-${day}`);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader bg="white">
            {isEditingDraft ? "Edit Draft Invoice" : "Create New Invoice"}
            <ModalCloseButton
              size="lg"
              color="gray.500"
              _hover={{ color: "gray.700" }}
              position="absolute"
              right={4}
              top={4}
            />
          </ModalHeader>
          <form onSubmit={formik.handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <HStack spacing={4} width="100%">
                  {isAdmin && (
                    <FormControl
                      isRequired
                      isInvalid={
                        formik.touched.clientId && formik.errors.clientId
                      }
                    >
                      <FormLabel>Client</FormLabel>
                      <Select
                        name="clientId"
                        value={formik.values.clientId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Select client"
                        isDisabled={isLoading}
                      >
                        {isLoading ? (
                          <option disabled>Loading clients...</option>
                        ) : (
                          clients.map((client) => (
                            <option key={client._id} value={client._id}>
                              {client.companyName}
                            </option>
                          ))
                        )}
                      </Select>
                      <FormErrorMessage>
                        {formik.errors.clientId}
                      </FormErrorMessage>
                    </FormControl>
                  )}

                  <FormControl
                    isRequired
                    isInvalid={
                      formik.touched.referenceNumber &&
                      formik.errors.referenceNumber
                    }
                  >
                    <FormLabel>Reference Number</FormLabel>
                    <Input
                      name="referenceNumber"
                      value={formik.values.referenceNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>
                      {formik.errors.referenceNumber}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack spacing={4} width="100%">
                  <FormControl
                    isRequired
                    isInvalid={
                      formik.touched.debtorName && formik.errors.debtorName
                    }
                  >
                    <FormLabel>Debtor Name</FormLabel>
                    <Input
                      name="debtorName"
                      value={formik.values.debtorName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>
                      {formik.errors.debtorName}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={formik.touched.amount && formik.errors.amount}
                  >
                    <FormLabel>Amount</FormLabel>
                    <Input
                      name="amount"
                      type="number"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FormErrorMessage>{formik.errors.amount}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack spacing={4} width="100%">
                  <FormControl
                    isRequired
                    isInvalid={formik.touched.date && formik.errors.date}
                  >
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger>
                        <Input
                          name="date"
                          value={formatDateForDisplay(formik.values.date)}
                          placeholder="dd/mm/yyyy"
                          readOnly
                          onClick={(e) => e.target.blur()}
                        />
                      </PopoverTrigger>
                      <PopoverContent width="auto" p={0}>
                        <PopoverBody>
                          <Calendar
                            onChange={(date) => handleDateChange(date, 'date')}
                            value={formik.values.date ? new Date(formik.values.date) : null}
                            locale="en-GB"
                          />
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                    <FormErrorMessage>{formik.errors.date}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired
                    isInvalid={
                      formik.touched.expectedDueDate &&
                      formik.errors.expectedDueDate
                    }
                  >
                    <FormLabel>Expected Due Date</FormLabel>
                    <Popover>
                      <PopoverTrigger>
                        <Input
                          name="expectedDueDate"
                          value={formatDateForDisplay(formik.values.expectedDueDate)}
                          placeholder="dd/mm/yyyy"
                          readOnly
                          onClick={(e) => e.target.blur()}
                        />
                      </PopoverTrigger>
                      <PopoverContent width="auto" p={0}>
                        <PopoverBody>
                          <Calendar
                            onChange={(date) => handleDateChange(date, 'expectedDueDate')}
                            value={formik.values.expectedDueDate ? new Date(formik.values.expectedDueDate) : null}
                            locale="en-GB"
                            minDate={formik.values.date ? new Date(formik.values.date) : null}
                          />
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                    <FormErrorMessage>
                      {formik.errors.expectedDueDate}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>

                <FormControl
                  isRequired
                  isInvalid={formik.touched.notes && formik.errors.notes}
                >
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    name="notes"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.notes}</FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={formik.touched.files && formik.errors.files}
                >
                  <FormLabel>Upload Documents</FormLabel>
                  <Box
                    border="1px dashed"
                    borderColor={isDragging ? "purple.500" : "gray.300"}
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                    bg={isDragging ? "purple.50" : "transparent"}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    transition="all 0.2s"
                  >
                    <Text fontSize="lg" mb={2}>
                      Drag And Drop Files Or Click Upload
                    </Text>
                    <Text fontSize="md" color="gray.500" mb={4}>
                      PDF, PNG And JPG
                    </Text>
                    <Button
                      as="label"
                      htmlFor="file-upload"
                      variant="outline"
                      cursor="pointer"
                    >
                      Upload File
                    </Button>
                    <Input
                      id="file-upload"
                      type="file"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const totalFiles = formik.values.files.length + files.length;
                        if (totalFiles > 5) {
                          showToast("Error", "Maximum 5 files allowed", "error");
                          return;
                        }
                        formik.setFieldValue("files", [
                          ...formik.values.files,
                          ...files,
                        ]);
                      }}
                      display="none"
                    />
                  </Box>
                  {formik?.values?.files?.length > 0 && (
                    <Box mt={4}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        Uploaded Files:
                      </Text>
                      {formik.values.files.map((file, index) => (
                        <HStack key={index} spacing={2} mb={1}>
                          <Text fontSize="sm">{file.name}</Text>
                          <Text fontSize="sm" color="gray.500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </Text>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              const newFiles = [...formik.values.files];
                              newFiles.splice(index, 1);
                              formik.setFieldValue("files", newFiles);
                              document.getElementById('file-upload').value = '';
                            }}
                          >
                            Remove
                          </Button>
                        </HStack>
                      ))}
                    </Box>
                  )}
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Attachments Can't Exceed 25MB Total (Max 5 files)
                  </Text>
                  <FormErrorMessage>{formik.errors.files}</FormErrorMessage>
                </FormControl>

                <Box bg="gray.200" p={2} borderRadius="md" width="100%">
                  <Text fontSize="xs" color="gray.600">
                    By Clicking The "Create" Button, You Will Send The Invoice
                    Information To The Factor. Please Ensure That All Invoice
                    Details Are Correct Before Proceeding. This Action Cannot Be
                    Undone.
                  </Text>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleClose}>
                Close
              </Button>
              <Button
                colorScheme="purple"
                type="submit"
                isLoading={formik.isSubmitting}
              >
                Create Invoice
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Exit Confirmation Dialog */}
      <Modal isOpen={showExitDialog} onClose={() => setShowExitDialog(false)} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Changes?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Do you want to save this invoice as a draft before closing?
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleDiscardChanges}>
              Discard Changes
            </Button>
            <Tooltip
              label="Document can't be saved as draft, you have to reupload."
              isDisabled={isSavingDraft}
              hasArrow
              placement="top"
              openDelay={200}
            >
              <span>
                <Button
                  colorScheme="purple"
                  mr={3}
                  onClick={handleSaveAsDraft}
                  isLoading={isSavingDraft}
                  loadingText="Saving..."
                  aria-label="Save as Draft"
                  isDisabled={isSavingDraft}
                >
                  Save as Draft
                </Button>
              </span>
            </Tooltip>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateInvoiceModal;
